/**
 * Pulling data in from the Worker.
 *
 * Both paths are on-device parsing over a dumb relay: the Worker holds raw
 * payloads and forwards Oura calls, and every interpretation happens here. The
 * health inbox is acknowledged only after the records are durably stored, so a
 * dropped response costs a retry rather than a day of data.
 */
import { parseHealthPayload } from './applehealth';
import { parseOuraBundle } from './oura';
import { put } from './db';
import { mergeMetrics, refreshAll, saveRun, settings, updateSettings } from '../store';
import { addDays, nowIso, toDay } from '../lib/time';
import { newId } from '../lib/id';
import type { Reading } from './schema';

export interface SyncOutcome {
  ok: boolean;
  days: number;
  runs: number;
  unmapped: string[];
  message: string;
}

function endpointOf(): { base: string; token: string } | null {
  const s = settings.value.sync;
  if (!s.endpoint || !s.token) return null;
  return { base: s.endpoint.replace(/\/+$/, ''), token: s.token };
}

async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const cfg = endpointOf();
  if (!cfg) throw new Error('Set the sync endpoint and token in Settings first.');
  const res = await fetch(`${cfg.base}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      authorization: `Bearer ${cfg.token}`,
      'content-type': 'application/json',
    },
  });
  if (res.status === 401) throw new Error('The sync token was rejected.');
  return res;
}

/** Ask the Worker whether it is configured, without revealing any secret. */
export async function checkStatus(): Promise<{ ouraConfigured: boolean; queued: number; latest: string | null }> {
  const res = await apiFetch('/api/status');
  if (!res.ok) throw new Error(`Status check failed (${res.status})`);
  return res.json();
}

/** Collect whatever Health Auto Export has posted, store it, then acknowledge. */
export async function pullHealth(): Promise<SyncOutcome> {
  const res = await apiFetch('/api/health/pull');
  if (!res.ok) throw new Error(`Health pull failed (${res.status})`);
  const body = (await res.json()) as {
    payloads: { id: number; receivedAt: string; body: unknown }[];
  };

  if (!body.payloads.length) {
    return { ok: true, days: 0, runs: 0, unmapped: [], message: 'Nothing new waiting.' };
  }

  const allDays = new Map<string, Record<string, Reading>>();
  const unmapped = new Set<string>();
  let runs = 0;
  let maxId = 0;

  for (const payload of body.payloads) {
    maxId = Math.max(maxId, payload.id);
    const parsed = parseHealthPayload(payload.body);
    for (const name of parsed.unmapped) unmapped.add(name);
    for (const [day, fields] of parsed.days) {
      allDays.set(day, { ...(allDays.get(day) ?? {}), ...fields });
    }
    for (const run of parsed.runs) {
      await saveRun(run);
      runs++;
    }
    // Keep the raw payload briefly so an unmapped field can be inspected.
    if (parsed.unmapped.length) {
      await put('raw', {
        id: newId(), at: nowIso(), kind: 'apple_health',
        unmapped: parsed.unmapped, body: payload.body,
      });
    }
  }

  const days = await mergeMetrics(allDays);

  // Only now is it safe to let the server forget these.
  if (maxId > 0) {
    await apiFetch('/api/health/ack', { method: 'POST', body: JSON.stringify({ through: maxId }) });
  }
  await updateSettings({ sync: { ...settings.value.sync, lastHealthSyncAt: nowIso() } });
  await refreshAll();

  return {
    ok: true, days, runs, unmapped: [...unmapped],
    message: `${days} ${days === 1 ? 'day' : 'days'} updated${runs ? `, ${runs} runs` : ''}.`,
  };
}

const OURA_COLLECTIONS = ['daily_sleep', 'daily_readiness', 'daily_activity', 'sleep', 'daily_spo2'] as const;

/** Pull the last `days` of Oura through the proxy. */
export async function pullOura(days = 14): Promise<SyncOutcome> {
  const end = toDay();
  const start = addDays(end, -days);
  const bundle: Record<string, unknown> = {};

  for (const collection of OURA_COLLECTIONS) {
    const res = await apiFetch(`/api/oura/${collection}?start_date=${start}&end_date=${end}`);
    if (!res.ok) {
      const detail = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(detail.error ?? `Oura ${collection} failed (${res.status})`);
    }
    bundle[collection] = await res.json();
  }

  const parsed = parseOuraBundle(bundle, nowIso());
  const touched = await mergeMetrics(parsed.days);
  await updateSettings({ sync: { ...settings.value.sync, lastOuraSyncAt: nowIso() } });
  await refreshAll();

  return {
    ok: true, days: touched, runs: 0, unmapped: [],
    message: `${touched} ${touched === 1 ? 'day' : 'days'} updated from Oura.`,
  };
}
